import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";
import Spinner from "../components/Spinner";
import PurchaseSpecPanel from "../components/PurchaseSpecPanel";
import AuthImage from "../components/AuthImage";
import {CartIcon} from "../components/icons";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface Recommendation {
    key: string;
    type: string;
    title: string;
    detail: string;
    targetItemId: string | null;
    /** Уверенность движка 0..1 (additive-поле единого движка рекомендаций). */
    confidence?: number;
    /** Причины рекомендации (объяснимость); additive-поле. */
    reasons?: string[];
    /**
     * FILL_GAP (gap-analysis-v2): категория-представитель функциональной роли (additive-поле;
     * панель подбора MR-2 работает по roleCode из ключа, категория остаётся в DTO для истории).
     */
    suggestedCategoryId?: string | null;
    /** Карточка знает вещь (recommendation-cards-v2, FR-1); additive-поля, для FILL_GAP null. */
    targetItemName?: string | null;
    targetItemImageKey?: string | null;
    targetItemCategoryName?: string | null;
    targetItemBrandName?: string | null;

    // --- Единое решение по вещи (item-decision-consistency, F02/F04/F09) ---

    /** Сигналы разошлись: вместо уверенного «Расстаться, 75 %» показывается «Проверить». */
    uncertain?: boolean;
    /** Подпись согласованного действия. */
    actionLabel?: string | null;
    /** Откуда взято решение: DECLUTTER_VERDICT | AI_ADVICE | RECOMMENDATION_FEED. */
    decisionSource?: string | null;
    /** Когда решение посчитано. */
    decisionComputedAt?: string | null;
    /** До трёх конкретных вещей, закрывающих ту же функциональную роль. */
    alternatives?: Alternative[];
    /** Что потеряется при расставании. */
    lossSummary?: string | null;
}

interface Alternative {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    score: number | null;
    roleNameRu: string | null;
}

interface Feed {
    items: Recommendation[];
    pendingCount: number;
    acceptedCount: number;
    dismissedCount: number;
    consentRequired?: boolean;
    /** F03: NOT_CHECKED | PARTIAL | NO_MATCHES | MATCHES. */
    duplicateCheckState?: string | null;
    duplicateCoveragePercent?: number;
    duplicatesUnindexedCount?: number;
}

/** F02: откуда взято решение — пользователь вправе знать, какой расчёт с ним говорит. */
const SOURCE_LABEL: Record<string, string> = {
    DECLUTTER_VERDICT: 'из разбора гардероба',
    AI_ADVICE: 'из AI-анализа вещи',
    RECOMMENDATION_FEED: 'из анализа гардероба',
};

/** F03: состояние проверки дублей — «совпадений нет» и «не искали» больше не одно и то же. */
const DUPLICATE_STATE_TEXT: Record<string, string> = {
    NOT_CHECKED: 'Похожие вещи не проверяли: индекс гардероба не построен',
    PARTIAL: 'Похожие вещи проверены частично',
    NO_MATCHES: 'Похожие вещи проверены — совпадений нет',
    MATCHES: 'Есть похожие вещи',
};

const TYPE_LABEL: Record<string, string> = {
    DISPOSE: 'К выбытию',
    FILL_GAP: 'Пробел',
    MOVE_TO_HOME: 'В домашние',
    MOVE_TO_WORK: 'В рабочие',
    REDISCOVER: 'Забытая вещь',
    REPLACE: 'Обновление',
};

// Кнопка accept называет реальное действие карточки (dispose-refresh-recs, FR-5.2):
// DISPOSE — обратимое решение расстаться (вещь ждёт передачи), REPLACE — пункт списка покупок,
// MOVE_TO_HOME/MOVE_TO_WORK — перевод по назначению; для остальных — generic «Принять».
const ACCEPT_LABEL: Record<string, string> = {
    DISPOSE: 'Решить расстаться',
    REPLACE: 'В список покупок',
    MOVE_TO_HOME: 'В домашние',
    MOVE_TO_WORK: 'В рабочие',
};

/**
 * Блок «карточка знает вещь» (recommendation-cards-v2, FR-2): миниатюра, название-ссылка
 * на карточку вещи и подпись категория/бренд. Для REPLACE — с префиксом «Что меняем:».
 * Карточки без target-полей (FILL_GAP, legacy-ответы) блок не рендерят.
 */
const TargetItemBlock: React.FC<{rec: Recommendation}> = ({rec}) => {
    if (!rec.targetItemId || !rec.targetItemName) {
        return null;
    }
    const caption = [rec.targetItemCategoryName, rec.targetItemBrandName]
        .filter(Boolean).join(' · ');
    return (
        <div className="mt-2 flex items-center gap-3" data-testid="recommendation-target">
            {rec.targetItemImageKey && (
                <Link to={`/clothing/${rec.targetItemId}`} className="shrink-0"
                      data-testid="recommendation-target-photo">
                    <AuthImage
                        imageKey={rec.targetItemImageKey}
                        variant="thumbnail"
                        alt={rec.targetItemName}
                        className="w-14 h-14 rounded-lg object-cover"
                    />
                </Link>
            )}
            <div className="min-w-0 text-sm">
                {rec.type === 'REPLACE' && (
                    <span className="text-gray-500 dark:text-gray-400">Что меняем: </span>
                )}
                <Link
                    to={`/clothing/${rec.targetItemId}`}
                    className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                    data-testid="recommendation-target-link"
                >
                    {rec.targetItemName}
                </Link>
                {caption && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate"
                       data-testid="recommendation-target-caption">
                        {caption}
                    </p>
                )}
            </div>
        </div>
    );
};

/**
 * F04: функциональный избыток доказывается вещами, а не количеством. Наличие 28 пар обуви
 * ничего не говорит о взаимозаменяемости конкретной пары — поэтому здесь имена, фото и
 * честный ответ «что потеряется».
 */
const AlternativesBlock: React.FC<{rec: Recommendation}> = ({rec}) => {
    const alternatives = rec.alternatives ?? [];
    if (alternatives.length === 0) {
        return null;
    }
    return (
        <div className="mt-2 rounded-xl bg-gray-50 dark:bg-ink-800 p-3" data-testid="recommendation-alternatives">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                Ту же функцию закрывают:
            </p>
            <ul className="mt-2 space-y-2">
                {alternatives.map(alt => (
                    <li key={alt.itemId} className="flex items-center gap-2">
                        {alt.primaryPhotoUrl && (
                            <AuthImage imageKey={alt.primaryPhotoUrl} variant="thumbnail" alt={alt.name}
                                className="w-10 h-10 rounded-lg object-cover shrink-0"/>
                        )}
                        <Link to={`/clothing/${alt.itemId}`}
                            className="text-sm text-brand-600 dark:text-brand-400 hover:underline truncate">
                            {alt.name}
                        </Link>
                        {alt.score != null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                                {alt.score} из 100
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            {rec.lossSummary && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400"
                   data-testid="recommendation-loss">{rec.lossSummary}</p>
            )}
        </div>
    );
};

const RecommendationsPage: React.FC = () => {
    const {selectedOwnerId, selectedOwnerDisplayName, isOwnWardrobe} = useWardrobe();

    const [data, setData] = useState<Feed | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get<Feed>('/recommendations', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    useDocumentTitle();

    useEffect(() => {
        load();
    }, [load]);

    const decide = async (rec: Recommendation, action: 'ACCEPTED' | 'DISMISSED') => {
        setBusyKey(rec.key);
        setError(false);
        try {
            const api = await createAxiosInstance();
            // Решение фиксируется только в собственном гардеробе (owner = текущий пользователь).
            const res = await api.post<Feed>('/recommendations/decision', {
                key: rec.key,
                type: rec.type,
                action,
            });
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setBusyKey(null);
        }
    };

    if (loading) {
        return <Spinner/>;
    }

    if (error && !data) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Не удалось загрузить рекомендации</p>
                <button onClick={load} className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    Повторить
                </button>
            </div>
        );
    }

    const feed = data!;
    const title = isOwnWardrobe
        ? 'Рекомендации'
        : `Рекомендации: ${selectedOwnerDisplayName || selectedOwnerId?.slice(0, 8) + '...'}`;

    if (feed.consentRequired) {
        return (
            <div className="p-4 md:p-6 space-y-6 max-w-2xl">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
                <section
                    className="rounded-xl border border-gray-200 dark:border-ink-700 p-5 space-y-3"
                    data-testid="recommendation-consent-required"
                >
                    <p className="text-gray-700 dark:text-gray-200">
                        {isOwnWardrobe
                            ? 'Персональные рекомендации выключены: вы отозвали согласие на их формирование. Система не анализирует ваш гардероб для подсказок.'
                            : 'Владелец гардероба отключил персональные рекомендации.'}
                    </p>
                    {isOwnWardrobe && (
                        <Link
                            to="/profile"
                            className="inline-flex min-h-[44px] items-center px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                            data-testid="recommendation-consent-link"
                        >
                            Включить в настройках приватности
                        </Link>
                    )}
                </section>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-2xl">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                    Что система предлагает сделать с гардеробом сегодня. Принимайте полезное —
                    так система учится вашим предпочтениям.
                </p>
            </div>

            <div className="flex flex-wrap gap-2 text-sm" data-testid="recommendation-counts">
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-700 dark:text-gray-200">
                    Открытых: {feed.pendingCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                    Принято: {feed.acceptedCount}
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-ink-700 text-gray-500 dark:text-gray-400">
                    Отклонено: {feed.dismissedCount}
                </span>
            </div>

            {/* F03: охват проверки дублей рядом с рекомендациями, а не вместо них. */}
            {feed.duplicateCheckState && (
                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="recommendation-duplicate-state">
                    {DUPLICATE_STATE_TEXT[feed.duplicateCheckState] ?? 'Состояние проверки дублей неизвестно'}
                    {' · охват '}{feed.duplicateCoveragePercent ?? 0}%
                    {feed.duplicateCheckState !== 'NO_MATCHES' && (
                        <>
                            {' · '}
                            <Link to="/indexing" className="text-brand-600 dark:text-brand-400 hover:underline">
                                Проиндексировать
                            </Link>
                        </>
                    )}
                </p>
            )}

            {error && (
                <p className="text-rose-600 dark:text-rose-400" data-testid="recommendation-error">
                    Не удалось выполнить действие. Попробуйте ещё раз.
                </p>
            )}

            {feed.items.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400" data-testid="recommendation-empty">
                    Открытых рекомендаций нет — гардероб под контролем. 👌
                </p>
            ) : (
                <ul className="space-y-3" data-testid="recommendation-list">
                    {feed.items.map(rec => (
                        <li key={rec.key}
                            className="rounded-xl border border-gray-200 dark:border-ink-700 p-3 flex flex-col gap-2">
                            <div>
                                <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-xs text-gray-600 dark:text-gray-300 mr-2">
                                    {TYPE_LABEL[rec.type] || rec.type}
                                </span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">{rec.title}</span>
                                {rec.uncertain ? (
                                    <span
                                        className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-xs text-amber-800 dark:text-amber-300"
                                        title="Сигналы по этой вещи расходятся — уверенного совета сейчас нет"
                                        data-testid="recommendation-uncertain"
                                    >
                                        Проверить
                                    </span>
                                ) : typeof rec.confidence === 'number' && (
                                    <span
                                        className="ml-2 px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-500/15 text-xs text-brand-700 dark:text-brand-300"
                                        title="Уверенность рекомендации"
                                        data-testid="recommendation-confidence"
                                    >
                                        {Math.round(rec.confidence * 100)}%
                                    </span>
                                )}
                                <TargetItemBlock rec={rec}/>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{rec.detail}</p>
                                {/* F09: 2–3 причины человеческим языком; арифметика — по раскрытию. */}
                                {rec.reasons && rec.reasons.length > 0 && (
                                    <ul
                                        className="mt-1 text-xs text-gray-500 dark:text-gray-400 list-disc list-inside space-y-0.5"
                                        data-testid="recommendation-reasons"
                                    >
                                        {rec.reasons.slice(0, 3).map(reason => (
                                            <li key={reason}>{reason}</li>
                                        ))}
                                    </ul>
                                )}
                                <AlternativesBlock rec={rec}/>
                                {rec.reasons && rec.reasons.length > 3 && (
                                    <details className="mt-1" data-testid="recommendation-details">
                                        <summary className="inline-flex min-h-[44px] items-center text-xs text-gray-500 dark:text-gray-400 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
                                            Детали расчёта
                                        </summary>
                                        <ul className="mt-1 text-xs text-gray-500 dark:text-gray-400 list-disc list-inside space-y-0.5">
                                            {rec.reasons.slice(3).map(reason => (
                                                <li key={reason}>{reason}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                                {/* Кнопка называет то, что произойдёт («Решить расстаться» —
                                    обратимо, вещь остаётся у вас); рекомендованное действие —
                                    отдельной строкой, чтобы одно не выдавалось за другое. */}
                                {rec.actionLabel && (
                                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300"
                                       data-testid="recommendation-action">
                                        Рекомендация: <span className="font-medium">{rec.actionLabel}</span>
                                        {rec.decisionSource && SOURCE_LABEL[rec.decisionSource] && (
                                            <span className="text-gray-400 dark:text-gray-500">
                                                {' · '}{SOURCE_LABEL[rec.decisionSource]}
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                            {isOwnWardrobe && (
                                <div className="flex flex-col gap-2 sm:flex-row" data-testid="recommendation-actions">
                                    <button
                                        type="button"
                                        onClick={() => decide(rec, 'ACCEPTED')}
                                        disabled={busyKey === rec.key}
                                        className="min-h-[44px] px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                                    >
                                        {ACCEPT_LABEL[rec.type] || 'Принять'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => decide(rec, 'DISMISSED')}
                                        disabled={busyKey === rec.key}
                                        className="min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-700 dark:text-gray-200 hover:border-brand-500 disabled:opacity-50"
                                    >
                                        Отклонить
                                    </button>
                                    {rec.type === 'FILL_GAP' && (
                                        <button
                                            type="button"
                                            onClick={() => setExpandedKey(prev => (prev === rec.key ? null : rec.key))}
                                            aria-expanded={expandedKey === rec.key}
                                            className="inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-700 dark:text-gray-200 hover:border-brand-500"
                                            data-testid="shortlist-toggle"
                                        >
                                            <CartIcon size={18}/> Подобрать покупку
                                        </button>
                                    )}
                                </div>
                            )}
                            {isOwnWardrobe && rec.type === 'FILL_GAP' && expandedKey === rec.key && (
                                <PurchaseSpecPanel
                                    recKey={rec.key}
                                    recType={rec.type}
                                    roleCode={rec.key.split(':')[1]}
                                    onAccepted={() => {
                                        setExpandedKey(null);
                                        load();
                                    }}
                                    onDismiss={() => {
                                        setExpandedKey(null);
                                        decide(rec, 'DISMISSED');
                                    }}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default RecommendationsPage;
